import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PromotionModal from '../components/PromotionModal';
import TemplateModal from '../components/TemplateModal';
import DeleteConfirmModal from '../components/DeleteConfirmModal';
import './Promotions.css';

const Promotions = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedType, setSelectedType] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isTemplateModalOpen, setIsTemplateModalOpen] = useState(false);
  const [isTemplateSelectModalOpen, setIsTemplateSelectModalOpen] = useState(false);
  const [editingPromotion, setEditingPromotion] = useState(null);
  const [promotionToDelete, setPromotionToDelete] = useState(null);
  const [promotionForTemplate, setPromotionForTemplate] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState(null);

  // Mock promotional templates (MARKETING category)
  const [promotionalTemplates, setPromotionalTemplates] = useState([
    {
      id: 101,
      name: 'summer_sale_promo',
      displayName: 'Summer Sale Promotion',
      category: 'MARKETING',
      language: 'en',
      status: 'APPROVED',
      components: {
        header: { type: 'TEXT', text: '🌞 Summer Sale - 50% Off!' },
        body: {
          text: 'Hi {{1}}, get {{2}}% off on all summer collection items. Use code {{3}} at checkout. Limited time offer!'
        },
        footer: { text: 'Terms and conditions apply.' },
        buttons: [
          { type: 'URL', text: 'Shop Now', url: 'https://shop.example.com' }
        ]
      },
      variables: ['customer_name', 'discount_percentage', 'promo_code'],
      approvedAt: '2024-01-10T10:00:00',
      createdAt: '2024-01-08T14:30:00',
      submittedAt: '2024-01-08T14:30:00'
    },
    {
      id: 102,
      name: 'flash_sale_alert',
      displayName: 'Flash Sale Alert',
      category: 'MARKETING',
      language: 'en',
      status: 'PENDING',
      components: {
        header: null,
        body: {
          text: '⚡ Flash Sale! Get {{1}}% off on all electronics. First {{2}} customers only!'
        },
        footer: { text: 'Hurry, limited stock!' },
        buttons: [
          { type: 'QUICK_REPLY', text: 'Shop Now' },
          { type: 'URL', text: 'View Products', url: 'https://shop.example.com/electronics' }
        ]
      },
      variables: ['discount_percentage', 'customer_limit'],
      approvedAt: null,
      createdAt: '2024-01-15T16:45:00',
      submittedAt: '2024-01-15T16:45:00'
    }
  ]);

  // Mock promotions data
  const [promotions, setPromotions] = useState([
    {
      id: 1,
      title: 'Summer Sale - 50% Off',
      type: 'Discount',
      status: 'Active',
      discountType: 'percentage',
      discountValue: 50,
      code: 'SUMMER50',
      description: 'Get 50% off on all summer collection items. Limited time offer!',
      image: '🌞',
      startDate: '2024-01-15',
      endDate: '2024-02-15',
      targetAudience: 'All Customers',
      sentTo: 1500,
      clicked: 456,
      converted: 234,
      revenue: 12500,
      createdAt: '2024-01-10T09:00:00'
    },
    {
      id: 2,
      title: 'Flash Sale - Electronics',
      type: 'Flash Sale',
      status: 'Active',
      discountType: 'percentage',
      discountValue: 30,
      code: 'FLASH30',
      description: 'Flash sale on all electronics. First 100 customers only!',
      image: '⚡',
      startDate: '2024-01-20',
      endDate: '2024-01-22',
      targetAudience: 'Active Customers',
      sentTo: 800,
      clicked: 289,
      converted: 145,
      revenue: 8700,
      createdAt: '2024-01-18T14:30:00'
    },
    {
      id: 3,
      title: 'New Product Launch - Premium Headphones',
      type: 'New Product',
      status: 'Scheduled',
      discountType: 'fixed',
      discountValue: 50,
      code: 'NEWPROD50',
      description: 'Introducing our new premium wireless headphones. Pre-order now and save $50!',
      image: '🎧',
      startDate: '2024-01-25',
      endDate: '2024-02-25',
      targetAudience: 'All Customers',
      sentTo: 0,
      clicked: 0,
      converted: 0,
      revenue: 0,
      createdAt: '2024-01-20T11:00:00'
    },
    {
      id: 4,
      title: 'Valentine\'s Day Special',
      type: 'Event',
      status: 'Draft',
      discountType: 'percentage',
      discountValue: 25,
      code: 'VALENTINE25',
      description: 'Celebrate Valentine\'s Day with special offers. Perfect gifts for your loved ones.',
      image: '💝',
      startDate: '2024-02-10',
      endDate: '2024-02-14',
      targetAudience: 'All Customers',
      sentTo: 0,
      clicked: 0,
      converted: 0,
      revenue: 0,
      createdAt: '2024-01-19T16:45:00'
    },
    {
      id: 5,
      title: 'Buy One Get One Free',
      type: 'Discount',
      status: 'Completed',
      discountType: 'bogo',
      discountValue: 100,
      code: 'BOGO100',
      description: 'Buy one item and get another one absolutely free!',
      image: '🎁',
      startDate: '2024-01-05',
      endDate: '2024-01-12',
      targetAudience: 'All Customers',
      sentTo: 2000,
      clicked: 678,
      converted: 389,
      revenue: 19500,
      createdAt: '2024-01-01T10:00:00',
      completedAt: '2024-01-12T23:59:59'
    },
    {
      id: 6,
      title: 'Free Shipping Weekend',
      type: 'Event',
      status: 'Active',
      discountType: 'shipping',
      discountValue: 100,
      code: 'FREESHIP',
      description: 'Free shipping on all orders this weekend. No minimum purchase required!',
      image: '🚚',
      startDate: '2024-01-20',
      endDate: '2024-01-21',
      targetAudience: 'All Customers',
      sentTo: 1200,
      clicked: 567,
      converted: 234,
      revenue: 11200,
      createdAt: '2024-01-17T09:30:00'
    }
  ]);

  const types = ['all', 'Discount', 'Flash Sale', 'New Product', 'Event'];
  const statuses = ['all', 'Draft', 'Scheduled', 'Active', 'Completed', 'Paused'];

  const filteredPromotions = promotions.filter(promotion => {
    const matchesSearch = 
      promotion.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      promotion.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
      promotion.code.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesType = 
      selectedType === 'all' || promotion.type === selectedType;
    
    const matchesStatus = 
      selectedStatus === 'all' || promotion.status === selectedStatus;

    return matchesSearch && matchesType && matchesStatus;
  });

  const handleCreatePromotion = () => {
    setEditingPromotion(null);
    setIsModalOpen(true);
  };

  const handleEditPromotion = (promotion) => {
    setEditingPromotion(promotion);
    setIsModalOpen(true);
  };

  const handleDeletePromotion = (promotion) => {
    setPromotionToDelete(promotion);
    setIsDeleteModalOpen(true);
  };

  const handleSavePromotion = (formData) => {
    if (editingPromotion) {
      // Update existing promotion
      setPromotions(prev => prev.map(p => 
        p.id === editingPromotion.id 
          ? { ...p, ...formData }
          : p
      ));
    } else {
      // Add new promotion with template information
      const selectedTemplate = promotionalTemplates.find(t => t.id === parseInt(formData.templateId));
      const newPromotion = {
        id: Math.max(...promotions.map(p => p.id), 0) + 1,
        ...formData,
        sentTo: 0,
        clicked: 0,
        converted: 0,
        revenue: 0,
        templateApprovedAt: selectedTemplate?.approvedAt || new Date().toISOString(),
        createdAt: new Date().toISOString()
      };
      setPromotions(prev => [newPromotion, ...prev]);
    }
  };

  const handleConfirmDelete = () => {
    if (promotionToDelete) {
      setPromotions(prev => prev.filter(p => p.id !== promotionToDelete.id));
      setIsDeleteModalOpen(false);
      setPromotionToDelete(null);
    }
  };

  const generateTemplateFromPromotion = (promotion) => {
    // Generate template name from promotion title
    const templateName = promotion.title.toLowerCase().replace(/[^a-z0-9]+/g, '_').substring(0, 50);
    
    // Generate body text with variables
    let bodyText = promotion.description;
    const variables = [];
    let varIndex = 1;
    
    // Add discount value as variable if it exists in description
    if (promotion.discountType === 'percentage' && bodyText.includes(promotion.discountValue)) {
      bodyText = bodyText.replace(promotion.discountValue.toString(), `{{${varIndex}}}`);
      variables.push('discount_percentage');
      varIndex++;
    } else if (promotion.discountType === 'fixed' && bodyText.includes(promotion.discountValue)) {
      bodyText = bodyText.replace(promotion.discountValue.toString(), `{{${varIndex}}}`);
      variables.push('discount_amount');
      varIndex++;
    }
    
    // Add promo code as variable if it exists in description
    if (promotion.code && bodyText.includes(promotion.code)) {
      bodyText = bodyText.replace(promotion.code, `{{${varIndex}}}`);
      variables.push('promo_code');
      varIndex++;
    }
    
    // If no variables were added, add promo code at the end
    if (variables.length === 0 && promotion.code) {
      bodyText += ` Use code {{${varIndex}}} to get this offer!`;
      variables.push('promo_code');
    }
    
    // Generate buttons
    const buttons = [];
    if (promotion.type === 'Flash Sale' || promotion.type === 'Discount') {
      buttons.push({ type: 'URL', text: 'Shop Now', url: 'https://shop.example.com' });
    }
    
    // Return in format expected by TemplateModal
    return {
      name: templateName,
      displayName: promotion.title,
      category: 'MARKETING',
      language: 'en',
      type: 'TEXT',
      headerType: 'TEXT',
      header: promotion.image ? `${promotion.image} ${promotion.title}` : promotion.title,
      body: bodyText || promotion.description,
      footer: promotion.type === 'Event' ? 'Limited time offer!' : '',
      variables: variables,
      buttons: buttons,
      components: {
        header: promotion.image ? { type: 'TEXT', text: `${promotion.image} ${promotion.title}` } : null,
        body: { text: bodyText || promotion.description },
        footer: promotion.type === 'Event' ? { text: 'Limited time offer!' } : null,
        buttons: buttons
      }
    };
  };

  const handleCreateTemplateFromPromotion = (promotion) => {
    setPromotionForTemplate(promotion);
    setIsTemplateModalOpen(true);
  };

  const handleSaveTemplate = (templateData) => {
    // Create template with PENDING status (needs Meta approval)
    const newTemplate = {
      id: Math.max(...promotionalTemplates.map(t => t.id), 0) + 1,
      ...templateData,
      status: 'PENDING',
      submittedAt: new Date().toISOString(),
      createdAt: new Date().toISOString(),
      approvedAt: null,
      components: {
        header: templateData.headerType === 'TEXT' && templateData.header 
          ? { type: 'TEXT', text: templateData.header }
          : templateData.headerType !== 'TEXT' && templateData.headerMedia
          ? { type: templateData.headerType, format: templateData.headerType }
          : null,
        body: {
          text: templateData.body
        },
        footer: templateData.footer ? { text: templateData.footer } : null,
        buttons: templateData.buttons || []
      }
    };
    setPromotionalTemplates(prev => [newTemplate, ...prev]);
    
    // Link template to promotion
    if (promotionForTemplate) {
      setPromotions(prev => prev.map(p => 
        p.id === promotionForTemplate.id 
          ? { ...p, templateId: newTemplate.id, templateStatus: 'PENDING' }
          : p
      ));
    }
    
    setIsTemplateModalOpen(false);
    setPromotionForTemplate(null);
  };

  const handleSubmitTemplateForApproval = (template) => {
    // In real app, this would call Meta API to submit template
    // For now, we'll simulate submission
    setPromotionalTemplates(prev => prev.map(t => 
      t.id === template.id 
        ? { ...t, status: 'PENDING', submittedAt: new Date().toISOString() }
        : t
    ));
  };

  const handleSelectTemplate = (promotion) => {
    setPromotionForTemplate(promotion);
    setIsTemplateSelectModalOpen(true);
  };

  const handleUseTemplate = (template) => {
    if (promotionForTemplate) {
      setPromotions(prev => prev.map(p => 
        p.id === promotionForTemplate.id 
          ? { ...p, templateId: template.id, templateStatus: template.status }
          : p
      ));
    }
    setSelectedTemplate(template);
    setIsTemplateSelectModalOpen(false);
    setPromotionForTemplate(null);
  };

  const handleSendPromotion = (promotion) => {
    // Check if promotion has an approved template
    const template = promotionalTemplates.find(t => t.id === promotion.templateId);
    
    if (!template) {
      alert('Please create and approve a template for this promotion first.');
      return;
    }
    
    if (template.status !== 'APPROVED') {
      alert(`Template "${template.displayName}" is ${template.status}. Only approved templates can be used for sending.`);
      return;
    }
    
    // In real app, this would send the promotion via WhatsApp Business API using the approved template
    setPromotions(prev => prev.map(p => 
      p.id === promotion.id 
        ? { ...p, status: 'Active' }
        : p
    ));
    navigate('/broadcast', { state: { promotion, template } });
  };

  const handlePausePromotion = (promotion) => {
    setPromotions(prev => prev.map(p => 
      p.id === promotion.id 
        ? { ...p, status: 'Paused' }
        : p
    ));
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(value);
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      'Draft': { color: '#6b7280', bg: '#f3f4f6' },
      'Scheduled': { color: '#f59e0b', bg: '#fef3c7' },
      'Active': { color: '#10b981', bg: '#d1fae5' },
      'Completed': { color: '#3b82f6', bg: '#dbeafe' },
      'Paused': { color: '#ef4444', bg: '#fee2e2' }
    };

    const config = statusConfig[status] || statusConfig['Draft'];
    return (
      <span 
        className="status-badge" 
        style={{ 
          background: config.bg, 
          color: config.color 
        }}
      >
        {status}
      </span>
    );
  };

  const getTypeBadge = (type) => {
    const typeConfig = {
      'Discount': { color: '#667eea', bg: '#e0e7ff' },
      'Flash Sale': { color: '#f59e0b', bg: '#fef3c7' },
      'New Product': { color: '#10b981', bg: '#d1fae5' },
      'Event': { color: '#ec4899', bg: '#fce7f3' }
    };

    const config = typeConfig[type] || typeConfig['Discount'];
    return (
      <span 
        className="type-badge" 
        style={{ 
          background: config.bg, 
          color: config.color 
        }}
      >
        {type}
      </span>
    );
  };

  const getDiscountDisplay = (promotion) => {
    if (promotion.discountType === 'percentage') {
      return `${promotion.discountValue}% OFF`;
    } else if (promotion.discountType === 'fixed') {
      return `$${promotion.discountValue} OFF`;
    } else if (promotion.discountType === 'bogo') {
      return 'BOGO';
    } else if (promotion.discountType === 'shipping') {
      return 'FREE SHIPPING';
    }
    return '';
  };

  const calculateConversionRate = (clicked, converted) => {
    if (clicked === 0) return 0;
    return ((converted / clicked) * 100).toFixed(1);
  };

  const calculateCTR = (sent, clicked) => {
    if (sent === 0) return 0;
    return ((clicked / sent) * 100).toFixed(1);
  };

  return (
    <div className="promotions-container">
      <div className="promotions-header">
        <div className="header-content">
          <h1>Promotions</h1>
          <p>Create and manage WhatsApp marketing campaigns and promotional offers</p>
        </div>
        <button className="btn-create-promotion" onClick={handleCreatePromotion}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="12" y1="5" x2="12" y2="19"></line>
            <line x1="5" y1="12" x2="19" y2="12"></line>
          </svg>
          Create Promotion
        </button>
      </div>

      <div className="promotions-toolbar">
        <div className="search-box">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="11" cy="11" r="8"></circle>
            <path d="m21 21-4.35-4.35"></path>
          </svg>
          <input
            type="text"
            placeholder="Search promotions by title, description, or code..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="search-input"
          />
        </div>

        <div className="filter-group">
          <label>Type:</label>
          <select
            value={selectedType}
            onChange={(e) => setSelectedType(e.target.value)}
            className="filter-select"
          >
            {types.map(type => (
              <option key={type} value={type}>
                {type === 'all' ? 'All Types' : type}
              </option>
            ))}
          </select>
        </div>

        <div className="filter-group">
          <label>Status:</label>
          <select
            value={selectedStatus}
            onChange={(e) => setSelectedStatus(e.target.value)}
            className="filter-select"
          >
            {statuses.map(status => (
              <option key={status} value={status}>
                {status === 'all' ? 'All Status' : status}
              </option>
            ))}
          </select>
        </div>

        <div className="promotions-stats">
          <span className="stat-item">
            Total: <strong>{promotions.length}</strong>
          </span>
          <span className="stat-item">
            Active: <strong>{promotions.filter(p => p.status === 'Active').length}</strong>
          </span>
          <span className="stat-item">
            Revenue: <strong>{formatCurrency(promotions.reduce((sum, p) => sum + p.revenue, 0))}</strong>
          </span>
        </div>
      </div>

      <div className="promotions-grid">
        {filteredPromotions.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">🎯</div>
            <h3>No promotions found</h3>
            <p>Create your first promotional campaign to start marketing</p>
            <button className="btn-add-first" onClick={handleCreatePromotion}>
              Create Promotion
            </button>
          </div>
        ) : (
          filteredPromotions.map(promotion => (
            <div key={promotion.id} className="promotion-card">
              <div className="promotion-header">
                <div className="promotion-image">
                  {promotion.image}
                </div>
                <div className="promotion-title-section">
                  <h3 className="promotion-title">{promotion.title}</h3>
                  <div className="promotion-badges">
                    {getTypeBadge(promotion.type)}
                    {getStatusBadge(promotion.status)}
                  </div>
                </div>
              </div>

              <div className="promotion-discount">
                <div className="discount-display">
                  {getDiscountDisplay(promotion)}
                </div>
                {promotion.code && (
                  <div className="promo-code">
                    Code: <strong>{promotion.code}</strong>
                  </div>
                )}
              </div>

              <div className="promotion-description">
                <p>{promotion.description}</p>
              </div>

              {promotion.status === 'Draft' && (
                <div className="promotion-workflow">
                  <div className="workflow-steps">
                    <div className={`workflow-step ${promotion.templateId ? 'completed' : 'active'}`}>
                      <div className="step-icon">
                        {promotion.templateId ? (
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <polyline points="20 6 9 17 4 12"></polyline>
                          </svg>
                        ) : (
                          <span>1</span>
                        )}
                      </div>
                      <span className="step-label">Create Template</span>
                    </div>
                    <div className={`workflow-step ${promotion.templateStatus === 'APPROVED' ? 'completed' : promotion.templateStatus === 'PENDING' ? 'active' : ''}`}>
                      <div className="step-icon">
                        {promotion.templateStatus === 'APPROVED' ? (
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <polyline points="20 6 9 17 4 12"></polyline>
                          </svg>
                        ) : (
                          <span>2</span>
                        )}
                      </div>
                      <span className="step-label">Meta Approval</span>
                    </div>
                    <div className={`workflow-step ${promotion.templateStatus === 'APPROVED' ? 'active' : ''}`}>
                      <div className="step-icon">
                        <span>3</span>
                      </div>
                      <span className="step-label">Send Message</span>
                    </div>
                  </div>
                </div>
              )}

              <div className="promotion-dates">
                <div className="date-item">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                    <line x1="16" y1="2" x2="16" y2="6"></line>
                    <line x1="8" y1="2" x2="8" y2="6"></line>
                    <line x1="3" y1="10" x2="21" y2="10"></line>
                  </svg>
                  <span>{formatDate(promotion.startDate)} - {formatDate(promotion.endDate)}</span>
                </div>
                <div className="date-item">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                    <circle cx="9" cy="7" r="4"></circle>
                    <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                    <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                  </svg>
                  <span>Target: {promotion.targetAudience}</span>
                </div>
              </div>

              {promotion.templateId && (
                <div className={`promotion-template-status ${promotion.templateStatus?.toLowerCase() || 'pending'}`}>
                  <div className="template-status-text">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                      <polyline points="14 2 14 8 20 8"></polyline>
                    </svg>
                    Template Status: <strong>{promotion.templateStatus || 'PENDING'}</strong>
                    {promotion.templateStatus === 'PENDING' && (
                      <span style={{ marginLeft: '8px', fontSize: '11px' }}>
                        (Awaiting Meta approval)
                      </span>
                    )}
                  </div>
                </div>
              )}

              {promotion.status === 'Active' || promotion.status === 'Completed' ? (
                <div className="promotion-stats">
                  <div className="stat-row">
                    <div className="stat-box">
                      <span className="stat-label">Sent</span>
                      <span className="stat-value">{promotion.sentTo.toLocaleString()}</span>
                    </div>
                    <div className="stat-box">
                      <span className="stat-label">Clicked</span>
                      <span className="stat-value">{promotion.clicked.toLocaleString()}</span>
                      <span className="stat-percentage">({calculateCTR(promotion.sentTo, promotion.clicked)}% CTR)</span>
                    </div>
                    <div className="stat-box">
                      <span className="stat-label">Converted</span>
                      <span className="stat-value">{promotion.converted.toLocaleString()}</span>
                      <span className="stat-percentage">({calculateConversionRate(promotion.clicked, promotion.converted)}% CVR)</span>
                    </div>
                    <div className="stat-box revenue">
                      <span className="stat-label">Revenue</span>
                      <span className="stat-value">{formatCurrency(promotion.revenue)}</span>
                    </div>
                  </div>
                </div>
              ) : null}

              <div className="promotion-actions">
                {promotion.status === 'Draft' && (
                  <>
                    {!promotion.templateId ? (
                      <button
                        className="action-btn template-btn"
                        onClick={() => handleCreateTemplateFromPromotion(promotion)}
                        title="Create Template"
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                          <polyline points="14 2 14 8 20 8"></polyline>
                          <line x1="16" y1="13" x2="8" y2="13"></line>
                          <line x1="16" y1="17" x2="8" y2="17"></line>
                        </svg>
                        Create Template
                      </button>
                    ) : (
                      <>
                        {promotion.templateStatus === 'PENDING' && (
                          <button
                            className="action-btn pending-btn"
                            onClick={() => handleSelectTemplate(promotion)}
                            title="Template Pending Approval"
                          >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <circle cx="12" cy="12" r="10"></circle>
                              <polyline points="12 6 12 12 16 14"></polyline>
                            </svg>
                            Pending Approval
                          </button>
                        )}
                        {promotion.templateStatus === 'APPROVED' && (
                          <button
                            className="action-btn send-btn"
                            onClick={() => handleSendPromotion(promotion)}
                            title="Send Promotion"
                          >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <line x1="22" y1="2" x2="11" y2="13"></line>
                              <polygon points="22 2 15 22 11 13 2 9 22 2"></polygon>
                            </svg>
                            Send
                          </button>
                        )}
                        {promotion.templateStatus === 'REJECTED' && (
                          <button
                            className="action-btn reject-btn"
                            onClick={() => handleCreateTemplateFromPromotion(promotion)}
                            title="Template Rejected - Create New"
                          >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <circle cx="12" cy="12" r="10"></circle>
                              <line x1="15" y1="9" x2="9" y2="15"></line>
                              <line x1="9" y1="9" x2="15" y2="15"></line>
                            </svg>
                            Rejected - Recreate
                          </button>
                        )}
                      </>
                    )}
                  </>
                )}
                {promotion.status === 'Scheduled' && (
                  <button
                    className="action-btn activate-btn"
                    onClick={() => handleSendPromotion(promotion)}
                    title="Activate Now"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <polyline points="20 6 9 17 4 12"></polyline>
                    </svg>
                    Activate
                  </button>
                )}
                {promotion.status === 'Active' && (
                  <button
                    className="action-btn pause-btn"
                    onClick={() => handlePausePromotion(promotion)}
                    title="Pause Promotion"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <rect x="6" y="4" width="4" height="16"></rect>
                      <rect x="14" y="4" width="4" height="16"></rect>
                    </svg>
                    Pause
                  </button>
                )}
                <button
                  className="action-btn edit-btn"
                  onClick={() => handleEditPromotion(promotion)}
                  title="Edit Promotion"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                  </svg>
                </button>
                <button
                  className="action-btn delete-btn"
                  onClick={() => handleDeletePromotion(promotion)}
                  title="Delete Promotion"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="3 6 5 6 21 6"></polyline>
                    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                  </svg>
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      <PromotionModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setEditingPromotion(null);
        }}
        onSave={handleSavePromotion}
        promotion={editingPromotion}
        availableTemplates={promotionalTemplates}
      />

      <DeleteConfirmModal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setPromotionToDelete(null);
        }}
        onConfirm={handleConfirmDelete}
        customerName={promotionToDelete?.title || ''}
        type="promotion"
      />

      <TemplateModal
        isOpen={isTemplateModalOpen}
        onClose={() => {
          setIsTemplateModalOpen(false);
          setPromotionForTemplate(null);
        }}
        onSave={handleSaveTemplate}
        template={promotionForTemplate ? generateTemplateFromPromotion(promotionForTemplate) : null}
        isPromotional={true}
      />

      {/* Template Selection Modal */}
      {isTemplateSelectModalOpen && (
        <div className="modal-overlay" onClick={() => setIsTemplateSelectModalOpen(false)}>
          <div className="modal-content template-select-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Select Approved Template</h2>
              <button className="modal-close" onClick={() => setIsTemplateSelectModalOpen(false)}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            </div>
            <div className="template-select-content">
              <p className="select-hint">Only approved templates can be used for sending promotional messages.</p>
              <div className="templates-list">
                {promotionalTemplates
                  .filter(t => t.status === 'APPROVED')
                  .map(template => (
                    <div 
                      key={template.id} 
                      className="template-select-item"
                      onClick={() => handleUseTemplate(template)}
                    >
                      <div className="template-select-header">
                        <h4>{template.displayName}</h4>
                        <span className="status-badge approved">APPROVED</span>
                      </div>
                      <div className="template-select-preview">
                        <p>{template.components?.body?.text || 'No body text'}</p>
                        {template.components?.buttons && template.components.buttons.length > 0 && (
                          <div className="template-buttons-preview">
                            {template.components.buttons.map((btn, idx) => (
                              <span key={idx} className="button-badge">
                                {btn.text}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                {promotionalTemplates.filter(t => t.status === 'APPROVED').length === 0 && (
                  <div className="empty-templates">
                    <p>No approved templates available. Please create and submit a template for Meta approval first.</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Promotions;



